import { useNavigate, Link } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "framer-motion";
import { Mail, Lock, CheckCircle, AlertCircle,Eye, EyeOff, } from "lucide-react";
import { useState, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import { loginService, getUserDetailsService, type LoginRequest, getLoginHistoryService, logoutService, sendResetLinkService, sendOtpService, resendOtpService } from "@/services/auth";
import { loginSchema, type LoginFormData } from "@/lib/validations";
import { getUserIdFromToken, isTokenExpired } from "@/lib/jwt";
import { storage } from "@/lib/storage";
import { setAuthTokenCookie, setRefreshTokenCookie } from "@/lib/clientCookie";
import { FormField, Button } from "@/components/ui";
import { useDispatch } from 'react-redux';
import { updateUserData } from "@/lib/store/slices/UserSlice";
import { setPermissions } from "@/lib/store/slices/PermissionsSlice";
import { getScreensPermissions } from "@/services/masters";
import { getDeliveryBoyDetailsService } from "@/services/deliveryAgent";
import { colors, fontSize, fontWeight, textColor, bgColor, borderColor } from "@/theme";
import { z } from "zod";

export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
});

export type ForgotPasswordFormData = z.infer<
  typeof forgotPasswordSchema
>;

function getDashboardPath(userData?: any): string {
  if (userData?.isSuperuser) return '/admin-dashboard';
  const r = (userData?.role || userData?.user?.role)?.toLowerCase().replace(/[\s_]+/g, '');
  if (r === 'parent') return '/dashboard';
  if (r === 'zonemanager') return '/zone-manager-dashboard';
  if (r === 'branchincharge') return '/branch-incharge-dashboard';
  if (r === 'deliveryboy') return '/delivery/dashboard';
  if (r === 'telecaller') return '/telecaller-dashboard';
  return '/admin-dashboard';
}

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch()
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [previousSession, setPreviousSession] = useState<any>(null);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetMethod, setResetMethod] = useState<"LINK" | "OTP">("LINK");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [otpTimer, setOtpTimer] = useState(30);
  const [canResendOtp, setCanResendOtp] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    if (showOtpModal && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }

    if (otpTimer === 0) {
      setCanResendOtp(true);
    }

    return () => clearInterval(interval);
  }, [showOtpModal, otpTimer]);

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: "onSubmit",
    defaultValues: {
      credential: "",
      password: "",
    },
  });

  const {
    control: forgotControl,
    handleSubmit: handleForgotSubmit,
    reset: resetForgotForm,
    formState: { errors: forgotErrors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  useEffect(() => {
    const token = storage.getAuthToken();
    if (token && !isTokenExpired(token)) {
      const userData = storage.getUserData();
      navigate(getDashboardPath(userData), { replace: true });
      return;
    }
    // No valid token — clear any stale data
    storage.clearAuthToken();
    storage.setRefreshToken('');
    setRefreshTokenCookie('');
    setAuthTokenCookie('');
  }, [navigate]);

  // Enhanced error handler for API responses
  const handleApiErrors = useCallback((response: any) => {
    clearErrors();
    setGeneralError("");

    const data = response?.errors ||
      response?.data?.errors ||
      response?.data.error ||
      {};
    if (!data || Object.keys(data).length === 0) {
      setGeneralError(response?.message || response?.error || "Login failed");
      return;
    }

    // Handle field-specific errors
    const fieldMapping: Record<string, string> = {
      username: "credential",
      password: "password"
    };

    Object.entries(data).forEach(([key, messages]: [string, any]) => {
      const errorMessage = Array.isArray(messages) ? messages[0] : messages;
      const fieldName = fieldMapping[key] || key;

      setError(fieldName as any, {
        type: "server",
        message: errorMessage
      });
    });
    if (data.error) {
      const errorMsg = Array.isArray(data.error)
        ? data.error[0]
        : data.error;

      setGeneralError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    // Handle non-field errors
    if (data.non_field_errors) {
      const errorMessage = Array.isArray(data.non_field_errors)
        ? data.non_field_errors[0]
        : data.non_field_errors;
      setGeneralError(errorMessage);
    }
  }, [clearErrors, setError]);

  const getApiMessage = (response: any): string => {
    const msg = response?.Message || response?.message || "An error occurred";
  if (!msg) return "Something went wrong";

  if (Array.isArray(msg)) {
    return msg[0];
  }

  if (typeof msg === "string") {
    // Handle stringified array
    if (msg.startsWith("[") && msg.endsWith("]")) {
      try {
        return msg
          .replace(/^\[/, "")
          .replace(/\]$/, "")
          .replace(/^['"]/, "")
          .replace(/['"]$/, "");
      } catch {
        return msg;
      }
    }

    return msg;
  }

  return String(msg);
};

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    setGeneralError("");
    clearErrors();

    try {
      // Always send as username, regardless of email or username format
      const credentials: LoginRequest = {
        username: data.credential,
        password: data.password,
      };
      const response = await loginService(credentials);

      if (response.status === 200 && (response.data as any)?.access) {
        const userData = response.data as any;
        const accessToken = userData.access;


        storage.setAuthToken(accessToken);
        setAuthTokenCookie(accessToken);

        if (userData.refresh) {
          storage.setRefreshToken(userData.refresh);
          setRefreshTokenCookie(userData.refresh);
        }

        const userId = getUserIdFromToken(accessToken);
        if (userId) {
          storage.setUserId(userId);
        }

        const userDetailsResponse = await getUserDetailsService(Number(userId));
        if (userDetailsResponse.status === 200 && userDetailsResponse.data) {
          const details = userDetailsResponse.data as any;
          const role = details?.role || details?.user?.role;

          if (role === "Delivery Boy") {
            try {
              const historyRes = await getLoginHistoryService();

              if (historyRes.status === 200 && Array.isArray(historyRes.data)) {
                const currentRefresh = storage.getRefreshToken();

                const activeSessions = historyRes.data.filter(
                  (item: any) =>
                    item.is_active === true &&
                    item.refresh_token !== currentRefresh
                );

                if (activeSessions.length > 0) {
                  const sortedSessions = activeSessions.sort(
                    (a: any, b: any) =>
                      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                  );

                  setPreviousSession({
                    sessions: sortedSessions,
                    firstSession: sortedSessions[0],
                    userDetails: details,
                  });

                  setShowSessionModal(true);
                  return;
                }
              }
            } catch (err) {
              console.error("Login history check failed:", err);
            }
            const uname = details?.username || details?.user?.username || data.credential;
            const deliveryRes = await getDeliveryBoyDetailsService(uname);
            const deliveryData = deliveryRes?.data;
            const hasShiftRecord =
              deliveryData &&
              (Array.isArray(deliveryData)
                ? (deliveryData as any[]).length > 0
                : Object.keys(deliveryData as any).length > 0);
            navigate(hasShiftRecord ? "/delivery/dashboard" : "/delivery/shift-start");
            return;
          }

          dispatch(updateUserData(details));
          storage.setUserData(details);

          try {
            const permRes = await getScreensPermissions();
            if (permRes.status === 200 && permRes.data) {
              const perms = (permRes.data as any).permissions ?? {};
              storage.setPermissions(perms);
              dispatch(setPermissions(perms));
            }
          } catch { /* non-blocking */ }

          navigate(getDashboardPath(details));
          return;
        }
        else if (userDetailsResponse.errors) {
          const errors = userDetailsResponse.errors;

          if (errors.non_field_errors) {
            const errorMsg = Array.isArray(errors.non_field_errors)
              ? errors.non_field_errors[0]
              : errors.non_field_errors;
            setGeneralError(errorMsg);
            toast.error(errorMsg);
          } else {
            handleApiErrors(userDetailsResponse.errors);
          }
          return;
        }
        else {
          dispatch(updateUserData(null));
          setGeneralError("Failed to fetch user details. Please try again.");
          toast.error("Failed to fetch user details");
          return;
        }
      }

      handleApiErrors(response);

    } catch (err: any) {
      console.error("Login error:", err);

      if (!err.response || err.response.status === 0) {
        setGeneralError("Network error. Please check your connection.");
        toast.error("Network error. Please try again.");
        return;
      }

      if (err.response?.status >= 400 && err.response?.data) {
        handleApiErrors(err.response);
        return;
      }

      const errorMsg = err.message || "Login failed. Please try again.";
      setGeneralError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueLogin = async () => {
    try {
      const sessions = previousSession?.sessions || [];

      for (const session of sessions) {
        try {
          await logoutService(session.refresh_token);
        } catch (err) {
          console.error("Failed to logout session:", session.id);
        }
      }

      const details = previousSession?.userDetails;
      const uname = details?.username || details?.user?.username;

      const deliveryRes = await getDeliveryBoyDetailsService(uname);
      const deliveryData = deliveryRes?.data;

      const hasShiftRecord =
        deliveryData &&
        (Array.isArray(deliveryData)
          ? deliveryData.length > 0
          : Object.keys(deliveryData).length > 0);

      navigate(hasShiftRecord ? "/delivery/dashboard" : "/delivery/shift-start");

    } catch (err) {
      console.error("Error handling sessions", err);
    } finally {
      setShowSessionModal(false);
    }
  };

  const onForgotPasswordSubmit = async (
    data: ForgotPasswordFormData
  ) => {
    setForgotLoading(true);
    setOtpError("");
    setGeneralError("");

    try {
      const payload = {
        Email: data.email,
        Method: resetMethod,
      };

      const res = await sendResetLinkService(payload);
      const response = res.data as any;

      if (response?.Status === false) {
        const message = getApiMessage(response) || "Request failed";

        setOtpError(message);
        toast.error(message);
        return;
      } else {
        setForgotEmail(data.email);

        // LINK FLOW
        if (resetMethod === "LINK") {
          setShowForgotModal(false);
          setShowSuccessModal(true);

          toast.success("Reset link sent successfully");
        }

        // OTP FLOW
        if (resetMethod === "OTP") {
          setShowForgotModal(false);
          setShowOtpModal(true);

          setOtp(["", "", "", "", "", ""]);
          setOtpTimer(30);
          setCanResendOtp(false);

          toast.success("OTP sent successfully");
        }

        resetForgotForm();
        return;
      }
      // toast.error(
      //   response?.data?.message ||
      //   "Failed to send reset request"
      // );

    } catch (error: any) {
      console.error(error);

      const errors =
        error?.response?.data?.errors ||
        error?.response?.data ||
        {};

      // NON FIELD ERRORS
      if (errors?.non_field_errors) {
        const msg = Array.isArray(errors.non_field_errors)
          ? errors.non_field_errors[0]
          : errors.non_field_errors;

        setOtpError(msg);
        toast.error(msg);
        return;
      }

      // NORMAL ERRORS
      if (errors?.Email) {
        const msg = Array.isArray(errors.Email)
          ? errors.Email[0]
          : errors.Email;

        setOtpError(msg);
        toast.error(msg);
        return;
      }

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to process request";

      setOtpError(errorMessage);
      toast.error(errorMessage);

    } finally {
      setForgotLoading(false);
    }
  };

  const handleOtpChange = (
    value: string,
    index: number
  ) => {
    if (!/^\d*$/.test(value)) return;

    const updatedOtp = [...otp];
    updatedOtp[index] = value.slice(-1);

    setOtp(updatedOtp);

    // Auto focus next
    if (value && index < 5) {
      const nextInput = document.getElementById(
        `otp-${index + 1}`
      );

      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (
      e.key === "Backspace" &&
      !otp[index] &&
      index > 0
    ) {
      const prevInput = document.getElementById(
        `otp-${index - 1}`
      );

      prevInput?.focus();
    }
  };

  const handleResendOtp = async () => {
    try {
      setOtpLoading(true);

      const res = await resendOtpService({
        Email: forgotEmail,
      });
      const response = res.data as any
      if (response?.Status === false) {
        const message =
          getApiMessage(response) ||
          "Request failed";

        setOtpError(message);
        toast.error(message);
        return;
      } else {
        toast.success("OTP resent successfully");

        setOtpTimer(30);
        setCanResendOtp(false);
      }

    } catch (error: any) {
      const errors =
        error?.response?.data?.errors ||
        error?.response?.data ||
        {};

      if (errors?.non_field_errors) {
        const msg = Array.isArray(errors.non_field_errors)
          ? errors.non_field_errors[0]
          : errors.non_field_errors;

        toast.error(msg);
        return;
      }

      toast.error(
        error?.response?.data?.message ||
        "Failed to resend OTP"
      );
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setOtpLoading(true);
    setOtpError("");

    try {
      const enteredOtp = otp.join("");

      if (enteredOtp.length !== 6) {
        setOtpError("Please enter valid 6 digit OTP");
        return;
      }

      if (!newPassword) {
        setOtpError("Password is required");
        return;
      }


      const payload = {
        Email: forgotEmail,
        OTP: enteredOtp,
        Password: newPassword,
      };

      const res = await sendOtpService(payload);
      const response = res.data as any;

      if (response?.Status === false) {
        const message =
          getApiMessage(response) ||
          "Request failed";

        setOtpError(message);
        toast.error(message);
        return;
      } else {
        toast.success("Password reset successful");

        setShowOtpModal(false);

        setOtp(["", "", "", "", "", ""]);
        setNewPassword("");
        setOtpError("");

        return;
      }

    } catch (error: any) {
      console.error(error);

      const errors =
        error?.response?.data?.errors ||
        error?.response?.data ||
        {};

      // NON FIELD ERRORS
      if (errors?.non_field_errors) {
        const msg = Array.isArray(errors.non_field_errors)
          ? errors.non_field_errors[0]
          : errors.non_field_errors;

        setOtpError(msg);
        toast.error(msg);
        return;
      }

      // FIELD ERRORS
      if (errors?.OTP) {
        const msg = Array.isArray(errors.OTP)
          ? errors.OTP[0]
          : errors.OTP;

        setOtpError(msg);
        toast.error(msg);
        return;
      }

      if (errors?.Password) {
        const msg = Array.isArray(errors.Password)
          ? errors.Password[0]
          : errors.Password;

        setOtpError(msg);
        toast.error(msg);
        return;
      }

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to reset password";

      setOtpError(errorMessage);
      toast.error(errorMessage);

    } finally {
      setOtpLoading(false);
    }
  };

  const handleCancelLogin = async () => {
    try {
      const currentRefresh = storage.getRefreshToken();

      // ✅ logout CURRENT session
      if (currentRefresh) {
        await logoutService(currentRefresh);
      }

    } catch (err) {
      console.error("Cancel login failed", err);
    } finally {
      // 🔥 clear all auth data
      storage.clearAuthToken();
      storage.setRefreshToken('');

      setAuthTokenCookie('');
      setRefreshTokenCookie('');

      storage.logout(); // if exists

      setShowSessionModal(false);

      // ✅ stay on login page (NO APP NAVIGATION)
      navigate("/login", { replace: true });
    }
  };

  const handleOtpPaste = (
    e: React.ClipboardEvent<HTMLInputElement>
  ) => {
    e.preventDefault();

    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (!pastedData) return;

    const newOtp = ["", "", "", "", "", ""];

    pastedData.split("").forEach((digit, index) => {
      if (index < 6) {
        newOtp[index] = digit;
      }
    });

    setOtp(newOtp);

    // focus last filled box
    const lastIndex = Math.min(
      pastedData.length - 1,
      5
    );

    const nextInput = document.getElementById(
      `otp-${lastIndex}`
    );

    nextInput?.focus();
  };

  return (
    <div className={`min-h-screen grid lg:grid-cols-2 bg-gradient-to-br from-orange-50 to-yellow-50`}>
      {/* LEFT PANEL */}
      <div
        className={`hidden lg:flex flex-col justify-center px-20 py-20 ${textColor.white}`}
        style={{ background: colors.brand.gradient }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-8">
            <p className={`${fontSize.sm} ${fontWeight.semibold} tracking-widest mb-2 uppercase`}>MERA</p>
            <h1 className={`${fontSize['5xl']} ${fontWeight.bold} mb-2 leading-tight`}>LUNCH BOX</h1>
            <p className={`${fontSize.xl} italic text-white/90`}>"Healthy. Reliable. School-Ready."</p>
          </div>

          <p className={`${fontSize.lg} text-white/90 mb-12 max-w-lg leading-relaxed`}>
            Nutritious, home-cooked meals delivered safely to your child’s school — every single day.          </p>

          <ul className="space-y-4 text-white/90">
            {[
              "Fresh, home-cooked meals prepared daily",
              "Safe & hygienic handling from kitchen to school",
              "Dedicated delivery staff for timely school drop-off",
              "Hassle-free daily & monthly subscription plans",
              "Peace of mind for working parents"
            ].map((item, index) => (
              <motion.li
                key={item}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="flex items-start gap-3"
              >
                <CheckCircle className={`w-6 h-6 flex-shrink-0 mt-0.5 ${textColor.emerald}`} />
                <span>{item}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>

      {/* RIGHT PANEL */}
      <div className="flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md space-y-8"
        >
          {/* Logo */}
          <div className="flex justify-center">
            <div className={`w-32 h-32 relative rounded-full overflow-hidden border-4 ${borderColor.orange} shadow-2xl`}>
              <img
                src="/mlblogo.jpeg"
                alt="Mera Lunch Box"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Title */}
          <div className="text-center">
            <h2 className={`${fontSize['4xl']} ${fontWeight.bold} ${textColor.heading} mb-2`}>Welcome Back</h2>
            <p className={`${fontSize.lg} ${textColor.muted}`}>
              Sign in to your Mera Lunch Box account
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={control}
              name="credential"
              label="Email or Username or Phone Number"
              placeholder="Enter email or username or phone number"
              icon={<Mail className={`w-5 h-5 ${textColor.icon}`} />}
              disabled={loading}
              error={errors.credential?.message}
              onClear={() => {
                clearErrors("credential");
                setGeneralError("");
              }}
            />

            <FormField
              control={control}
              name="password"
              label="Password"
              placeholder="••••••••"
              type="password"
              icon={<Lock className={`w-5 h-5 ${textColor.icon}`} />}
              disabled={loading}
              error={errors.password?.message}
              onClear={() => {
                clearErrors("password");
                setGeneralError("");
              }}
            />

            {/* General Error Display - BOTTOM */}
            {generalError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className={`p-3 ${bgColor.dangerLight} border ${borderColor.danger} rounded-lg`}
              >
                <div className={`flex items-start gap-2 ${fontSize.sm} ${textColor.dangerSoft}`}>
                  <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>{generalError}</span>
                </div>
              </motion.div>
            )}

            <Button
              type="submit"
              disabled={loading || isSubmitting}
              size="lg"
              className="w-full"
              style={{ background: colors.brand.gradient }}
            >
              {loading || isSubmitting ? (
                <>
                  <svg className="w-5 h-5 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing In...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>
          <div className="flex flex-row justify-between items-center">
            <button
              type="button"
              onClick={() => setShowForgotModal(true)}
              className="text-sm font-medium text-orange-600 hover:text-orange-700 hover:underline"
            >
              Forgot Password?
            </button>


            {/* Footer */}
            <p className={`text-center ${fontSize.md} ${textColor.muted}`}>
              New User?{" "}
              <Link
                to="/register"
                className={`${fontWeight.semibold} hover:text-orange-600 hover:underline transition-colors duration-200`}
                style={{ color: colors.brand.primary }}
              >
                Register here
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
      {showSessionModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[400px] shadow-xl">
            <h3 className="text-lg font-semibold mb-2">
              Active Session Detected
            </h3>

            <p className="text-sm text-gray-600 mb-4">
              You are already logged in on another device
              ({previousSession?.firstSession?.user_agent || "Unknown Device"})
              <br /><br />
              If you continue, you will be logged out from that device.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelLogin}
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={handleContinueLogin}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      {showForgotModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-xl font-semibold mb-2">
              Forgot Password
            </h2>

            <p className="text-gray-600 text-sm mb-5">
              Enter your registered email address and we'll
              send you a password reset link.
            </p>

            <form
              onSubmit={handleForgotSubmit(onForgotPasswordSubmit)}
            >
              <FormField
                control={forgotControl}
                name="email"
                label="Email Address"
                placeholder="Enter your email"
                icon={<Mail className="w-5 h-5" />}
                error={forgotErrors.email?.message}
              />

              {/* Reset Method Selection */}
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Choose Reset Method
                </label>

                <div className="flex rounded-lg overflow-hidden border">
                  <button
                    type="button"
                    onClick={() => setResetMethod("LINK")}
                    className={`flex-1 py-3 text-sm font-medium transition ${resetMethod === "LINK"
                        ? "bg-orange-500 text-white"
                        : "bg-white text-gray-700"
                      }`}
                  >
                    🔗 Email Link
                  </button>

                  <button
                    type="button"
                    onClick={() => setResetMethod("OTP")}
                    className={`flex-1 py-3 text-sm font-medium transition ${resetMethod === "OTP"
                        ? "bg-orange-500 text-white"
                        : "bg-white text-gray-700"
                      }`}
                  >
                    🔐 OTP
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotModal(false);
                    resetForgotForm();
                  }}
                  className="px-4 py-2 border rounded-lg"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg"
                >
                  {forgotLoading
                    ? "Sending..."
                    : resetMethod === "LINK"
                      ? "Send Reset Link"
                      : "Send OTP"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />

            <h2 className="text-2xl font-semibold mb-3">
              Password Reset Link Sent
            </h2>

            <p className="text-gray-600 mb-6">
              If an account exists with the email address you
              entered, we've sent a password reset link to
              your email.
              <br />
              <br />
              Please check your inbox (and spam folder) and
              create a new
              password.
            </p>

            <button
              onClick={() => setShowSuccessModal(false)}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg"
            >
              Got It
            </button>
          </div>
        </div>
      )}

      {showOtpModal && (
        <div className="fixed inset-0 bg-black/50 z-50 overflow-y-auto">
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6">
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">

            <h2 className="text-2xl font-semibold mb-2">
              Verify OTP
            </h2>

            <p className="text-sm text-gray-500 mb-5">
              Enter the 6 digit OTP sent to your email
            </p>

            {/* OTP BOXES */}
            <div className="flex justify-center gap-2 mb-5">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  value={digit}
                  maxLength={1}
                  onPaste={handleOtpPaste}
                  onChange={(e) =>
                    handleOtpChange(
                      e.target.value,
                      index
                    )
                  }
                  onKeyDown={(e) =>
                    handleOtpKeyDown(e, index)
                  }
                  className="w-12 h-12 border rounded-lg text-center text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              ))}
            </div>

            {/* PASSWORD */}
            <div className="space-y-4">
  <div className="relative">
    <input
      type={showPassword ? "text" : "password"}
      placeholder="New Password"
      value={newPassword}
      onChange={(e) =>
        setNewPassword(e.target.value)
      }
      className="w-full border rounded-lg px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-orange-500"
    />

    <button
      type="button"
      onClick={() => setShowPassword((prev) => !prev)}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
      aria-label={
        showPassword
          ? "Hide password"
          : "Show password"
      }
    >
      {showPassword ? (
        <EyeOff className="w-5 h-5" />
      ) : (
        <Eye className="w-5 h-5" />
      )}
    </button>
  </div>
</div>

            {/* ERROR */}
            {otpError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {otpError}
              </div>
            )}

            {/* TIMER */}
            <div className="mt-4 text-center text-sm">
              {canResendOtp ? (
                <button
                  onClick={handleResendOtp}
                  disabled={otpLoading}
                  className="text-orange-600 font-medium hover:underline"
                >
                  Resend OTP
                </button>
              ) : (
                <span className="text-gray-500">
                  Resend OTP in {otpTimer}s
                </span>
              )}
            </div>

            {/* BUTTONS */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowOtpModal(false);
                  setNewPassword("")
                  setOtp(["", "", "", "", "", ""]);
                }}
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={handleVerifyOtp}
                disabled={otpLoading}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg"
              >
                {otpLoading
                  ? "Verifying..."
                  : "Submit"}
              </button>
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );
}
